import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import BulletinImage from './BulletinImage';
import SafeBulletinContent from './SafeBulletinContent';
import BulletinActions from './BulletinActions';
import BulletinLikeHandler from './BulletinLikeHandler';
import { X, Heart, Info, MoreVertical, Eye, Maximize2, Minimize2, MessageCircle, Bookmark, Video, Plus } from 'lucide-react';
import LoopComments from '@/components/loops/LoopComments';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { useIsMobile } from '@/hooks/use-mobile';
import { useMatureContent } from '@/hooks/use-mature-content';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';
import { useLightboxToggle } from '@/hooks/use-lightbox-toggle';
import { supabase } from '@/integrations/supabase/client';
import { SaveToPieMenu } from '@/components/moodboards/SaveToPieMenu';
import type { SaveToMoodboardPayload } from '@/types/moodboards';
import { useIsItemSaved, useRemoveFromMoodboards } from '@/hooks/use-moodboards';
import { LinkifyContent } from '@/components/shared/LinkifyContent';
import LightboxInfoPanel from './lightbox/LightboxInfoPanel';
import LightboxMobileSheet from './lightbox/LightboxMobileSheet';

export interface BulletinLightboxProps {
  bulletins: any[]; // ordered list currently rendered in feed
  selectedId: string;
  onNavigate: (id: string) => void; // navigate to adjacent id and update URL
  onClose: () => void; // close viewer and return to feed (no refresh)
  // Optional content bounds for desktop to respect left nav/content area
  contentBounds?: { top: number; left: number; width: number; height: number } | null;
  // Pre-loaded thumbnail from feed for progressive enhancement
  preloadedThumbnail?: string | null;
}

const BulletinLightbox: React.FC<BulletinLightboxProps> = ({
  bulletins,
  selectedId,
  onNavigate,
  onClose,
  contentBounds,
  preloadedThumbnail,
}) => {
  const isMobile = useIsMobile();
  const { showMatureContent } = useMatureContent();
  const { user } = useAuth();
  const { viewMode, toggleViewMode, isFitToHeight } = useLightboxToggle();

  const containerRef = useRef<HTMLDivElement>(null);
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const touchDelta = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  const index = Math.max(0, bulletins.findIndex((b) => b.id === selectedId));
  const bulletin = bulletins[index];

  const [isLoaded, setIsLoaded] = useState(false);
  // Load info panel state from localStorage, default to false for minimalism
  const [showInfo, setShowInfo] = useState(() => {
    const saved = localStorage.getItem('fomo:lightbox:showInfo');
    return saved === 'true';
  });
  const [showSheet, setShowSheet] = useState(false); // swipe-up details/comments
  const [showComments, setShowComments] = useState(false); // comments drawer
  const [showSaveMenu, setShowSaveMenu] = useState(false);
  const [saveMenuPosition, setSaveMenuPosition] = useState({ x: 0, y: 0 });
  const [hasLikedPost, setHasLikedPost] = useState(false);
  const [slideDirection, setSlideDirection] = useState<'up' | 'down'>('down');
  const queryClient = useQueryClient();
  // Live counters pulled from DB to ensure accuracy
  const [dbLikeCount, setDbLikeCount] = useState<number>(0);
  const [viewCount, setViewCount] = useState<number>(0);

  // Prime and prefetch author profiles so the info panel author loads instantly
  useEffect(() => {
    if (!bulletin) return;
    // Prime cache with embedded profile if present
    if (bulletin.user_id && bulletin.profiles) {
      queryClient.setQueryData(['profile', bulletin.user_id], bulletin.profiles);
    }
    // Only prefetch when info panel is visible (to avoid unnecessary work)
    if (!showInfo) return;
    const ids = new Set<string>();
    const cur = bulletin.user_id as string | undefined;
    const prev = index > 0 ? (bulletins[index - 1]?.user_id as string | undefined) : undefined;
    const next = index < bulletins.length - 1 ? (bulletins[index + 1]?.user_id as string | undefined) : undefined;
    [cur, prev, next].forEach((id) => { if (id) ids.add(id); });
    ids.forEach((id) => {
      queryClient.prefetchQuery({
        queryKey: ['profile', id],
        staleTime: 5 * 60 * 1000,
        queryFn: async () => {
          const { data, error } = await supabase
            .from('profiles')
            .select('id, username, profile_image_url, is_admin, is_premium, is_partner')
            .eq('id', id)
            .maybeSingle();
          if (error) throw error;
          return data;
        }
      });
    });
  }, [bulletin?.id, bulletin?.user_id, showInfo, index]);
  
  // Check if item is saved
  const { data: isSaved = false } = useIsItemSaved('bulletin', bulletin?.id);
  const removeFromMoodboards = useRemoveFromMoodboards();
  
  // Check if current user follows the bulletin author
  const { data: isFollowingAuthor = false } = useQuery({
    queryKey: ['follow-status', user?.id, bulletin?.user_id],
    queryFn: async () => {
      if (!user?.id || !bulletin?.user_id || user.id === bulletin.user_id) return false;
      
      const { data, error } = await supabase
        .from('followers')
        .select('*')
        .eq('follower_id', user.id)
        .eq('following_id', bulletin.user_id)
        .maybeSingle();
        
      if (error) {
        console.error('Error checking follow status:', error);
        return false;
      }
      
      return !!data;
    },
    enabled: !!user?.id && !!bulletin?.user_id && user.id !== bulletin.user_id
  });
  
  // Follow/Unfollow mutation
  const followMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id || !bulletin?.user_id) throw new Error('Not authenticated');
      
      if (isFollowingAuthor) {
        // Unfollow
        const { error } = await supabase
          .from('followers')
          .delete()
          .eq('follower_id', user.id)
          .eq('following_id', bulletin.user_id);
          
        if (error) throw error;
        return { action: 'unfollowed' };
      } else {
        // Follow
        const { error } = await supabase
          .from('followers')
          .insert({
            follower_id: user.id,
            following_id: bulletin.user_id
          });
          
        if (error) throw error;
        return { action: 'followed' };
      }
    },
    onSuccess: (data) => {
      toast.success(data.action === 'followed' ? 'Following!' : 'Unfollowed');
      queryClient.invalidateQueries({ queryKey: ['follow-status'] });
    },
    onError: (error) => {
      console.error('Error following/unfollowing:', error);
      toast.error('Failed to update follow status');
    }
  });
  
  // Show comments if bulletin has engagement
  const hasEngagement = (dbLikeCount > 0 || viewCount > 0);

  useEffect(() => {
    console.log('[BulletinLightbox] mount for', selectedId);
    const t = setTimeout(() => setIsLoaded(true), 180);
    // Reset hasLikedPost when bulletin changes
    setHasLikedPost(false);
    
    // Track view when lightbox opens
    const trackView = async () => {
      if (!bulletin?.loop_id) {
        console.warn('[Lightbox] No loop_id found, cannot track view');
        return;
      }
      
      try {
        const { error } = await supabase
          .from('loops_views')
          .insert({ 
            loop_id: bulletin.loop_id, 
            user_id: user?.id || null 
          });
        
        if (error && !error.message.includes('duplicate key')) {
          console.error('[Lightbox] Error tracking view:', error);
        } else {
          console.log('[Lightbox] View tracked successfully for loop:', bulletin.loop_id);
          // Refresh view count after a short delay to allow trigger to update
          setTimeout(() => {
            supabase
              .from('bulletins')
              .select('view_count')
              .eq('id', selectedId)
              .single()
              .then(({ data }) => {
                if (data) {
                  setViewCount(data.view_count);
                }
              });
          }, 500);
        }
      } catch (e) {
        console.error('[Lightbox] Unexpected error tracking view:', e);
      }
    };
    
    trackView();
    
    return () => clearTimeout(t);
  }, [selectedId, bulletin?.loop_id, user?.id]);

  // Refresh like/view counters from DB on open/change
  useEffect(() => {
    let mounted = true;
    const refreshCounts = async () => {
      try {
        const { data, error } = await supabase
          .from('bulletins')
          .select('like_count, view_count')
          .eq('id', selectedId)
          .single();
        if (!mounted) return;
        if (error) {
          console.warn('[Lightbox] Failed to fetch counters:', error.message);
          // fall back to props
          setDbLikeCount(bulletin?.like_count ?? 0);
          setViewCount(bulletin?.view_count ?? 0);
          return;
        }
        setDbLikeCount(data?.like_count ?? (bulletin?.like_count ?? 0));
        setViewCount(data?.view_count ?? (bulletin?.view_count ?? 0));
      } catch (e) {
        console.warn('[Lightbox] Counter refresh error', e);
        setDbLikeCount(bulletin?.like_count ?? 0);
        setViewCount(bulletin?.view_count ?? 0);
      }
    };
    refreshCounts();
    return () => { mounted = false; };
  }, [selectedId, bulletin?.like_count, bulletin?.view_count]);

  // Debug: log comment button visibility after counters load
  useEffect(() => {
    if (bulletin) {
      console.log('[Lightbox] Comments button check:', {
        hasEngagement,
        dbLikeCount,
        viewCount,
        loop_id: bulletin.loop_id,
        shouldShow: hasEngagement && !!bulletin.loop_id
      });
    }
  }, [hasEngagement, dbLikeCount, viewCount, bulletin]);

  // Keyboard navigation - TikTok-style vertical scrolling
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        e.preventDefault();
        const prevIndex = Math.max(0, index - 1);
        if (prevIndex !== index) {
          setSlideDirection('up');
          onNavigate(bulletins[prevIndex].id);
        }
      } else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        e.preventDefault();
        const nextIndex = Math.min(bulletins.length - 1, index + 1);
        if (nextIndex !== index) {
          setSlideDirection('down');
          onNavigate(bulletins[nextIndex].id);
        }
      } else if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [index, bulletins, onNavigate, onClose]);

  // Mouse wheel navigation - but allow scrolling inside info panel when hovering over it
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      // Check if mouse is hovering over the info panel or any scrollable area
      const elementAtMouse = document.elementFromPoint(e.clientX, e.clientY);
      if (!elementAtMouse) return;
      
      // Traverse up from element at mouse position to find scrollable containers
      let target = elementAtMouse as HTMLElement;
      while (target && target !== document.body) {
        const overflowY = window.getComputedStyle(target).overflowY;
        const isScrollable = overflowY === 'auto' || overflowY === 'scroll';
        
        if (isScrollable && target.scrollHeight > target.clientHeight) {
          // Mouse is over a scrollable container - allow natural scroll
          const isAtTop = target.scrollTop === 0;
          const isAtBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 1;
          
          // Only prevent navigation if there's more content to scroll
          if ((e.deltaY < 0 && !isAtTop) || (e.deltaY > 0 && !isAtBottom)) {
            return; // Let the scroll happen naturally
          }
        }
        target = target.parentElement as HTMLElement;
      }
      
      // Mouse not over scrollable area or at boundary - navigate
      if (Math.abs(e.deltaY) > 10) {
        if (e.deltaY < 0) {
          // Scrolling up
          const prevIndex = Math.max(0, index - 1);
          if (prevIndex !== index) {
            setSlideDirection('up');
            onNavigate(bulletins[prevIndex].id);
          }
        } else {
          // Scrolling down
          const nextIndex = Math.min(bulletins.length - 1, index + 1);
          if (nextIndex !== index) {
            setSlideDirection('down');
            onNavigate(bulletins[nextIndex].id);
          }
        }
      }
    };
    window.addEventListener('wheel', handleWheel, { passive: true });
    return () => window.removeEventListener('wheel', handleWheel);
  }, [index, bulletins, onNavigate]);

  const bounds = contentBounds || null;

  // Persist info panel state to localStorage
  const handleToggleInfo = useCallback(() => {
    setShowInfo((prev) => {
      const next = !prev;
      localStorage.setItem('fomo:lightbox:showInfo', String(next));
      return next;
    });
  }, []);

  const startTouch = (e: React.TouchEvent) => {
    const t = e.touches[0];
    touchStart.current = { x: t.clientX, y: t.clientY };
    touchDelta.current = { x: 0, y: 0 };
  };

  const moveTouch = (e: React.TouchEvent) => {
    if (!touchStart.current) return;
    const t = e.touches[0];
    touchDelta.current = { x: t.clientX - touchStart.current.x, y: t.clientY - touchStart.current.y };
  };

  const endTouch = (e: React.TouchEvent) => {
    if (!touchStart.current) return;
    const deltaY = touchDelta.current.y;
    const threshold = 50;

    if (Math.abs(deltaY) > threshold) {
      if (deltaY > 0) {
        // Swiped down → go to previous
        const prevIndex = Math.max(0, index - 1);
        if (prevIndex !== index) {
          setSlideDirection('up');
          onNavigate(bulletins[prevIndex].id);
        }
      } else {
        // Swiped up → go to next
        const nextIndex = Math.min(bulletins.length - 1, index + 1);
        if (nextIndex !== index) {
          setSlideDirection('down');
          onNavigate(bulletins[nextIndex].id);
        }
      }
    } else {
      // Horizontal swipe still works as fallback
      const { x } = touchDelta.current;
      const absX = Math.abs(x);
      const H_TH = 64; // horizontal swipe threshold
      if (absX > H_TH) {
        if (x < 0 && index < bulletins.length - 1) onNavigate(bulletins[index + 1].id);
        if (x > 0 && index > 0) onNavigate(bulletins[index - 1].id);
      }
    }

    touchStart.current = null;
    touchDelta.current = { x: 0, y: 0 };
  };

  if (!bulletin) return null;

  // Derive focal points for consistent positioning
  const focalX = bulletin.focal_point_x ?? 50;
  const focalY = bulletin.focal_point_y ?? 33; // Top-third default for better portrait framing
  const isMature = bulletin?.is_mature || bulletin?.moderation_result?.isNSFW || bulletin?.moderation_status === 'sensitive';

  // Layout frame respecting site content area on desktop
  const frameStyle: React.CSSProperties = bounds
    ? {
        position: 'fixed',
        top: bounds.top,
        left: bounds.left,
        width: bounds.width,
        height: bounds.height,
        zIndex: 60,
      }
    : {
        position: 'fixed',
        inset: 0,
        zIndex: 60,
      };

  return (
    <>
      {/* Solid page-like background */}
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="fixed inset-0 bg-[#1A1A1B]"
          style={{ zIndex: 50, pointerEvents: isLoaded ? 'auto' : 'none' }}
          onClick={(e) => {
            // When respecting bounds, only close if user taps outside the frame
            if (!bounds) onClose();
            // If bounds exist, clicks are ignored here; the frame handles closing via swipe/down
          }}
        />
      </AnimatePresence>

      {/* Page-like content container */}
      <motion.div
        ref={containerRef}
        className="flex items-center justify-center min-h-screen"
        style={frameStyle}
        initial={{ opacity: 0, y: isMobile ? 0 : 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: isMobile ? 0 : 20 }}
        transition={{ type: 'spring', stiffness: 400, damping: 32, mass: 0.8 }}
        onTouchStart={startTouch}
        onTouchMove={moveTouch}
        onTouchEnd={endTouch}
      >
        {/* Close button (top-right) */}
        <button
          aria-label="Close"
          className="absolute top-3 right-3 z-50 rounded-full bg-muted/60 hover:bg-muted/80 p-2 text-foreground/80 hover:text-foreground backdrop-blur-sm transition-colors"
          onClick={onClose}
        >
          <X size={18} />
        </button>

        {/* Core content with overlaid UI elements */}
        <div className="relative w-full h-full flex items-center justify-center">
          <BulletinLikeHandler
            bulletinId={bulletin.id}
            initialLikeCount={bulletin.like_count || 0}
            userId={user?.id}
            currentImageUrl={bulletin.image_url}
            currentThumbnailUrl={bulletin.thumbnail_url}
            loopId={bulletin.loop_id}
            onLike={() => setHasLikedPost(true)}
          >
            {(hasLiked, currentLikeCount, onDoubleTap) => (
              <>
                {/* Image container - full width with animation */}
                <AnimatePresence mode="wait">
                  <motion.div
                    key={bulletin.id}
                    initial={{ opacity: 0, y: slideDirection === 'down' ? 30 : -30 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: slideDirection === 'down' ? -30 : 30 }}
                    transition={{ duration: 0.2, ease: 'easeOut' }}
                    className="relative w-full flex items-center justify-center"
                  >
                    <BulletinImage
                      src={bulletin.image_url}
                      thumbnailSrc={preloadedThumbnail || bulletin.thumbnail_url}
                      fileType={bulletin.file_type}
                      isSelected={true}
                      onClick={() => {}}
                      onLoad={() => {}}
                      onError={() => {}}
                      isMature={isMature}
                      showMatureContent={showMatureContent}
                      size="large"
                      focalPointX={focalX}
                      focalPointY={focalY}
                      fillContainer={false}
                      viewMode={viewMode}
                    />
                  </motion.div>
                </AnimatePresence>

                {/* Right side controls - responsive positioning */}
                <div className={cn(
                  "absolute z-50 flex gap-2 transition-all duration-300",
                  isMobile 
                    ? "top-3 right-12 flex-row" // Mobile: top-right horizontal layout
                    : cn(
                        "top-1/2 -translate-y-1/2 flex-col", // Desktop: vertical layout
                        showInfo ? "right-[420px]" : "right-3"
                      )
                )}>
                  {/* Author avatar with follow button - only shown after liking */}
                  {hasLikedPost && bulletin?.profiles && (
                    <div className="relative group">
                      <Avatar className="w-10 h-10 border-2 border-muted/60 cursor-pointer hover:border-primary/80 transition-colors">
                        <AvatarImage src={bulletin.profiles.profile_image_url} />
                        <AvatarFallback className="bg-muted text-xs">
                          {bulletin.profiles.username?.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      {/* Only show follow button if not your own post and not already following */}
                      {user?.id !== bulletin?.user_id && !isFollowingAuthor && (
                        <button
                          onClick={() => followMutation.mutate()}
                          disabled={followMutation.isPending}
                          className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary/90 transition-colors shadow-lg"
                          aria-label="Follow"
                        >
                          <Plus size={12} strokeWidth={3} />
                        </button>
                      )}
                    </div>
                  )}
                  
                  <ToggleButton active={isFitToHeight} onClick={toggleViewMode} />
                  <InfoButton active={showInfo} onClick={handleToggleInfo} />
                  <VideoButton onClick={() => console.log('Video feature coming soon')} />
                </div>

                {/* Bottom description - responsive positioning and fade out when info panel open */}
                {bulletin.content && (
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={`content-${bulletin.id}`}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: showInfo ? 0 : 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className={cn(
                        "absolute z-50 max-w-md",
                        isMobile 
                          ? "bottom-32 left-4 right-4" // Mobile: above author with clearance
                          : "bottom-24 left-4 right-16" // Desktop: above author
                      )}
                    >
                      <SafeBulletinContent
                        content={bulletin.content}
                        size="large"
                        position="bottom-left"
                        hasImage={true}
                      />
                    </motion.div>
                  </AnimatePresence>
                )}

                {/* Bottom engagement bar - subtle and responsive */}
                {/* Mobile: offset above bottom nav + safe area (right aligned) */}
                <div 
                  className="absolute z-50 flex items-center gap-2 right-4 md:hidden"
                  style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 64px)' }}
                >
                  {/* Comments button - shown when bulletin has engagement and loop_id */}
                  {hasEngagement && bulletin.loop_id && (
                    <button
                      onClick={() => setShowComments(true)}
                      className="flex items-center gap-1 rounded-full bg-black/50 backdrop-blur-sm px-2 py-1 text-white/90 hover:text-white transition-colors"
                      aria-label="View comments"
                    >
                      <MessageCircle size={14} />
                    </button>
                  )}
                  
                  {/* Likes */}
                  <div className="flex items-center gap-1 rounded-full bg-black/50 backdrop-blur-sm px-2 py-1 text-white/90">
                    <button
                      aria-label="Like"
                      className={cn(
                        "transition-colors",
                        hasLiked 
                          ? "text-red-500 hover:text-red-400" 
                          : "text-white/90 hover:text-white"
                      )}
                      onClick={(e) => {
                        e.preventDefault();
                        onDoubleTap(e as unknown as React.MouseEvent);
                      }}
                    >
                      <Heart size={14} fill={hasLiked ? 'currentColor' : 'none'} />
                    </button>
                    <span className="text-xs tabular-nums">
                      {currentLikeCount ?? dbLikeCount ?? 0}
                    </span>
                  </div>

                  {/* Views */}
                  <div className="flex items-center gap-1 rounded-full bg-black/50 backdrop-blur-sm px-2 py-1 text-white/90">
                    <Eye size={14} />
                    <span className="text-xs tabular-nums">{viewCount ?? 0}</span>
                  </div>
                  
                  {/* Save to Moodboard */}
                  <button
                    onClick={(e) => {
                      // If already saved, unsave it
                      if (isSaved) {
                        removeFromMoodboards.mutate({
                          contentType: 'bulletin',
                          contentId: bulletin.id,
                        });
                        return;
                      }
                      
                      // Otherwise, show save menu
                      if (showSaveMenu) {
                        setShowSaveMenu(false);
                      } else {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setSaveMenuPosition({
                          x: rect.left + rect.width / 2,
                          y: rect.top + rect.height / 2,
                        });
                        setShowSaveMenu(true);
                      }
                    }}
                    className={cn(
                      "rounded-full bg-black/50 backdrop-blur-sm p-1.5 transition-colors",
                      isSaved ? "text-pink-400 hover:text-pink-300" : "text-white/90 hover:text-white"
                    )}
                    aria-label={isSaved ? "Remove from moodboards" : "Save to moodboard"}
                  >
                    <Bookmark size={14} fill={isSaved ? 'currentColor' : 'none'} />
                  </button>
                </div>

                {/* Desktop: bottom-right tight to edges */}
                <div 
                  className="absolute z-50 hidden md:flex items-center gap-2 bottom-4 right-4"
                >
                  {/* Comments button - shown when bulletin has engagement and loop_id */}
                  {hasEngagement && bulletin.loop_id && (
                    <button
                      onClick={() => setShowComments(true)}
                      className="flex items-center gap-1 rounded-full bg-black/50 backdrop-blur-sm px-2 py-1 text-white/90 hover:text-white transition-colors"
                      aria-label="View comments"
                    >
                      <MessageCircle size={14} />
                    </button>
                  )}
                  
                  {/* Likes */}
                  <div className="flex items-center gap-1 rounded-full bg-black/50 backdrop-blur-sm px-2 py-1 text-white/90">
                    <button
                      aria-label="Like"
                      className={cn(
                        "transition-colors",
                        hasLiked 
                          ? "text-red-500 hover:text-red-400" 
                          : "text-white/90 hover:text-white"
                      )}
                      onClick={(e) => {
                        e.preventDefault();
                        onDoubleTap(e as unknown as React.MouseEvent);
                      }}
                    >
                      <Heart size={14} fill={hasLiked ? 'currentColor' : 'none'} />
                    </button>
                    <span className="text-xs tabular-nums">
                      {currentLikeCount ?? dbLikeCount ?? 0}
                    </span>
                  </div>

                  {/* Views */}
                  <div className="flex items-center gap-1 rounded-full bg-black/50 backdrop-blur-sm px-2 py-1 text-white/90">
                    <Eye size={14} />
                    <span className="text-xs tabular-nums">{viewCount ?? 0}</span>
                  </div>
                  
                  {/* Save to Moodboard */}
                  <button
                    onClick={(e) => {
                      // If already saved, unsave it
                      if (isSaved) {
                        removeFromMoodboards.mutate({
                          contentType: 'bulletin',
                          contentId: bulletin.id,
                        });
                        return;
                      }
                      
                      // Otherwise, show save menu
                      if (showSaveMenu) {
                        setShowSaveMenu(false);
                      } else {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setSaveMenuPosition({
                          x: rect.left + rect.width / 2,
                          y: rect.top + rect.height / 2,
                        });
                        setShowSaveMenu(true);
                      }
                    }}
                    className={cn(
                      "rounded-full bg-black/50 backdrop-blur-sm p-1.5 transition-colors",
                      isSaved ? "text-pink-400 hover:text-pink-300" : "text-white/90 hover:text-white"
                    )}
                    aria-label={isSaved ? "Remove from moodboards" : "Save to moodboard"}
                  >
                    <Bookmark size={14} fill={isSaved ? 'currentColor' : 'none'} />
                  </button>
                </div>
              </>
            )}
          </BulletinLikeHandler>
          
          
        </div>

        {/* Foundation-style info panel - desktop only */}
        <LightboxInfoPanel show={showInfo && !isMobile} bulletin={bulletin} />

        {/* Mobile info sheet */}
        <LightboxMobileSheet show={showInfo && isMobile} bulletin={bulletin} onClose={handleToggleInfo} />

        {/* Bottom sheet for comments/description when swiped up */}
        <AnimatePresence>
          {showSheet && (
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', stiffness: 320, damping: 36 }}
              className="absolute bottom-0 left-0 right-0 max-h-[80%] bg-card border-t border-border text-foreground rounded-t-2xl shadow-2xl overflow-hidden"
              style={{ zIndex: 70 }}
            >
              <div className="p-4 border-b border-border flex items-center justify-between">
                <div className="w-8 h-1.5 bg-muted rounded-full mx-auto" />
                <button
                  className="absolute right-3 top-3 text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => setShowSheet(false)}
                >
                  <X size={18} />
                </button>
              </div>
              <div className="p-4 space-y-4 overflow-y-auto">
                {bulletin.content && (
                  <div>
                    <div className="text-muted-foreground text-xs mb-1">Description</div>
                    <div className="text-sm leading-relaxed whitespace-pre-wrap">
                      <LinkifyContent content={bulletin.content} authorId={bulletin.user_id} />
                    </div>
                  </div>
                )}
                {/* Comments could be plugged here in future without coupling */}
                <div className="text-muted-foreground text-xs">Comments coming soon</div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Comments drawer */}
      <Drawer open={showComments} onOpenChange={setShowComments}>
        <DrawerContent className="bg-[#1A1A1B] border-t-2 border-[#343536] max-h-[80vh]">
          <DrawerHeader>
            <DrawerTitle>Comments</DrawerTitle>
          </DrawerHeader>
          <div className="p-4 overflow-y-auto">
            {bulletin.loop_id ? (
              <LoopComments loopId={bulletin.loop_id} />
            ) : (
              <div className="text-center text-muted-foreground py-8">
                <p>Comments are not available for this post.</p>
                {bulletin.created_at && new Date(bulletin.created_at) < new Date('2025-01-01') && (
                  <p className="text-xs mt-2">(Legacy post created before comments integration)</p>
                )}
              </div>
            )}
          </div>
        </DrawerContent>
      </Drawer>

      {/* Save to Moodboard Pie Menu */}
      {bulletin && (
        <SaveToPieMenu
          isOpen={showSaveMenu}
          onClose={() => setShowSaveMenu(false)}
          position={saveMenuPosition}
          item={{
            content_type: 'bulletin',
            content_id: bulletin.id,
            image_url: bulletin.image_url || bulletin.thumbnail_url || '',
            author_id: bulletin.user_id,
            author_username: bulletin.profiles?.username,
            caption: bulletin.content,
            original_created_at: bulletin.created_at,
          }}
        />
      )}
    </>
  );
};

const ToggleButton = ({ active, onClick }: { active: boolean; onClick: () => void }) => (
  <button
    aria-label={active ? "Fit to width" : "Fit to height"}
    onClick={onClick}
    className={cn(
      'rounded-full p-2 text-foreground/80 hover:text-foreground transition-colors backdrop-blur-sm',
      active ? 'bg-primary/80 text-primary-foreground' : 'bg-muted/60 hover:bg-muted/80'
    )}
  >
    {active ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
  </button>
);

const InfoButton = ({ active, onClick }: { active: boolean; onClick: () => void }) => (
  <button
    aria-label="Info"
    onClick={onClick}
    className={cn(
      'rounded-full p-2 text-foreground/80 hover:text-foreground transition-colors backdrop-blur-sm',
      active ? 'bg-primary/80 text-primary-foreground' : 'bg-muted/60 hover:bg-muted/80'
    )}
  >
    <Info size={18} />
  </button>
);

const VideoButton = ({ onClick }: { onClick: () => void }) => (
  <button
    aria-label="Video (Coming Soon)"
    onClick={onClick}
    disabled
    className="rounded-full p-2 text-muted-foreground/40 cursor-not-allowed backdrop-blur-sm bg-muted/40"
  >
    <Video size={18} />
  </button>
);

export default BulletinLightbox;